import {
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  Popover,
  Select
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  CheckBoxOutlineBlankRounded,
  CheckBoxRounded,
  CheckRounded,
  KeyboardArrowRightRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ParamsProps {
  id: number;
  name: string;
}

interface HierarchyParamsProps<T> extends ParamsProps {
  value: string;
  hierarchyOptions: T[];
}

interface HierarchySelectProps<T> {
  placeholder: string;
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  required?: boolean;
  disabled?: boolean;
}

export default function HierarchySelect<T extends ParamsProps>({
  placeholder,
  options,
  value,
  onChange,
  required = false,
  disabled = false
}: HierarchySelectProps<T>) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [forceOpen, setForceOpen] = useState<boolean>(false);
  const [openCustom, setOpenCustom] = useState<boolean>(false);
  const [anchorCustom, setAnchorCustom] = useState<HTMLLIElement | null>(null);

  // Create a sort of the options array and provide new format to select
  const sortedOptions = [...options].sort((a, b) => a.id - b.id);
  const newOptions = getNewOptions(sortedOptions);

  // For find the current value and set the selected option
  const selectedIds = [...value]
    .sort((a, b) => a.id - b.id)
    .map((value) => value.id);
  const selectedIdText = selectedIds.join(', ');
  const currentSelectedText = getCurrentSelectedText(selectedIds, newOptions);

  // Create helper text for custom options
  const formHelperText =
    !Boolean(
      newOptions.find((option) => option.value === currentSelectedText)?.name
    ) &&
    sortedOptions
      .filter((option) => selectedIds.some((id) => id === option.id))
      .map((option) => option.name)
      .join(', ');

  function getNewOptions(sortedOptions: T[]): HierarchyParamsProps<T>[] {
    return sortedOptions.map((option, index, sortedOptions) => {
      const hierarchyOptions = sortedOptions.filter(
        (sortedOption) => sortedOption.id >= option.id
      );
      return {
        id: index,
        name: `${option.name}${
          hierarchyOptions.length > 1 ? t('hierarchySelect.andBelow') as string : t('hierarchySelect.only') as string
        }`,
        value: hierarchyOptions
          .map((sortedOption) => sortedOption.id)
          .join(', '),
        hierarchyOptions
      };
    });
  }

  function getCurrentSelectedText(
    selectedIds: number[],
    options: HierarchyParamsProps<T>[]
  ): string {
    const selectedIdText = selectedIds.join(', ');
    const currentOption = options.find(
      (option) => option.value === selectedIdText
    );
    if (currentOption) {
      return currentOption.value;
    } else {
      return selectedIds.length > 0 ? t('hierarchySelect.custom') as string : '';
    }
  }

  function handleGroupChange(
    event: React.ChangeEvent<{ value: unknown }>
  ): void {
    const newValue = event.target.value;
    if (newValue !== t('hierarchySelect.custom') as string) {
      const currentOption = newOptions.find(
        (option) => option.value === newValue
      );
      if (currentOption) {
        onChange(currentOption.hierarchyOptions);
      }
      setForceOpen(false);
    }
  }

  function handleCustomClick(event: React.MouseEvent<HTMLElement>): void {
    // To handle clicking on the `Custom` option from
    // (1) Click on the `Custom` option again and again
    // (2) Click inside the `Custom` menu, not on the background
    const target = event.target as HTMLInputElement | HTMLElement;
    const value = (target as HTMLInputElement).value; // When switch from another options, the value must be `Custom``
    const innerText = target.innerText; // When read the text in component, the innerText must be `Custom...`
    const customText = t('hierarchySelect.custom') as string;
    const customEllipsis = t('hierarchySelect.customEllipsis') as string;
    if (value === customText || innerText === customEllipsis) {
      // To prevent click on the disable custom option and selection still active
      if (!disabled) setForceOpen(true);
    } else {
      setForceOpen(false);
    }
  }

  function handleOneChange(newValue: number): void {
    const selectedId = selectedIds.find((id) => id === newValue);
    if (selectedId) {
      const newIds = value.filter((value) => value.id !== selectedId);
      onChange(newIds);
    } else {
      const newOption = options.find((option) => option.id === newValue);
      if (newOption) onChange([...value, newOption]);
    }
  }

  function handleOpen(): void {
    setOpen(true);
  }

  function handleClose(): void {
    setOpen(false);
    setForceOpen(false);
    setOpenCustom(false);
  }

  function handleOpenCustom(event: React.MouseEvent<HTMLLIElement>) {
    setAnchorCustom(event.currentTarget);
    setOpenCustom(true);
    setForceOpen(true);
  }

  return (
    <>
      <FormControl
        size="small"
        fullWidth
        required={required}
        disabled={disabled}
      >
        <Select
          className={clsx({
            [classes.placeholder]: currentSelectedText.length <= 0
          })}
          id="hierarchy-selection"
          displayEmpty
          variant="outlined"
          open={open || forceOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          value={currentSelectedText}
          onClick={handleCustomClick}
          onChange={handleGroupChange}
          renderValue={(selected) => {
            if ((selected as string).length === 0) {
              return `${placeholder}${required ? ' *' : ''}`;
            }
            const name = newOptions.find(
              (option) => option.value === currentSelectedText
            )?.name;
            return name ?? t('hierarchySelect.customEllipsis') as string;
          }}
        >
          {newOptions.map((option) => {
            const selected = option.value === selectedIdText;
            return (
              <MenuItem
                key={option.id}
                className={clsx(classes.menuItem, {
                  [classes.menuItemSelected]: selected
                })}
                value={option.value}
              >
                {selected && (
                  <CheckRounded className={classes.mr1} color="primary" />
                )}
                {option.name}
              </MenuItem>
            );
          })}
          <Divider className={classes.divider} />
          <MenuItem
            className={classes.menuItemCustom}
            value={t('hierarchySelect.custom') as string}
            onClick={handleOpenCustom}
          >
            <span className={classes.flexGrow}>{t('hierarchySelect.customEllipsis') as string}</span>
            <KeyboardArrowRightRounded color="action" />
          </MenuItem>
        </Select>
        <FormHelperText className={classes.formHelperText}>
          {formHelperText}
        </FormHelperText>
      </FormControl>
      <Popover
        open={openCustom}
        anchorEl={anchorCustom}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handleClose}
      >
        {sortedOptions.map((option) => {
          const selected = selectedIds.some((id) => id === option.id);
          return (
            <MenuItem
              key={option.id}
              className={classes.subMenuItem}
              onClick={() => handleOneChange(option.id)}
            >
              <Checkbox
                icon={<CheckBoxOutlineBlankRounded fontSize="small" />}
                checkedIcon={<CheckBoxRounded fontSize="small" />}
                checked={selected}
              />
              {option.name}
            </MenuItem>
          );
        })}
      </Popover>
    </>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    placeholder: {
      color: theme.palette.grey[theme.palette.type === 'light' ? 500 : 600]
    },
    menuItem: {
      padding: theme.spacing(0.75, 2, 0.75, 5.5)
    },
    menuItemSelected: {
      paddingLeft: theme.spacing(1.5)
    },
    menuItemCustom: {
      padding: theme.spacing(0.75, 1, 0.75, 2)
    },
    subMenuItem: {
      padding: theme.spacing(0, 2, 0, 0.5),
      '&:first-child': {
        marginTop: theme.spacing(1)
      },
      '&:last-child': {
        marginBottom: theme.spacing(1)
      }
    },
    formHelperText: {
      padding: theme.spacing(0, 1.75),
      lineHeight: '120%'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    flexGrow: {
      flexGrow: 1
    },
    mr1: {
      marginRight: theme.spacing(1)
    }
  })
);
